import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';
import { NativeSelect, NativeSelectOption } from '../native-select';
import { cn } from '../../lib/utils';

type Option = { value: string; label: string };

/**
 * Same data and styling as SelectTextField, but rendered as a real <select> — see the note in
 * native-country-field.tsx for why that matters to autofill.
 *
 * It is the better control for a short, closed list: the OS picker opens on the first tap with no
 * popover to position, and a list this size has nothing to type-to-search through. Reach for
 * SelectTextField when the list is long enough that searching it is the point.
 */
export default function NativeSelectTextField({
	label,
	options,
	placeholder,
	className,
	optional,
	autoComplete,
}: {
	label: string;
	options: Option[];
	/**
	 * Leading empty option, for a field whose value can legitimately be unset. Omit when the field
	 * always holds one of the options: an unselectable placeholder is only noise then.
	 */
	placeholder?: string;
	className?: string;
	optional?: boolean;
	autoComplete?: string;
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name, optional);

	return (
		<div className={cn('flex flex-col gap-2', className)}>
			{label && (
				<FieldLabelRow
					label={label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
				/>
			)}
			<NativeSelect
				className="w-full"
				id={field.name}
				name={field.name}
				autoComplete={autoComplete}
				aria-invalid={hasFieldError(field.state.meta)}
				disabled={formReadOnly}
				value={field.state.value ?? ''}
				onBlur={field.handleBlur}
				onChange={(event) => field.handleChange(event.target.value)}
			>
				{placeholder !== undefined && <NativeSelectOption value="">{placeholder}</NativeSelectOption>}
				{options.map((item) => (
					<NativeSelectOption key={item.value} value={item.value}>
						{item.label}
					</NativeSelectOption>
				))}
			</NativeSelect>
			<FieldInfo fieldState={field.state} />
		</div>
	);
}
